import React, { useState } from 'react';
import { FaChevronDown, FaMicrochip, FaMemory, FaClock } from 'react-icons/fa';
import { Card, Spinner } from './Primitives';
import {
  ProductBadges,
  PowerStateBadge,
  IdentityBadge,
  HeartbeatIndicator,
} from './VmMeta';
import VmDetailFields from './VmDetailFields';
import VmActions from './VmActions';
import VmIpAddress from './VmIpAddress';
import { useVmDetail } from '../hooks/useVmDetail';
import { powerStateMeta } from '../utils/constants';
import { formatMemoryMb, formatMhz, formatUptime } from '../utils/format';

/** Micro-statistica in linea (icona + valore + etichetta). */
function MiniStat({ icon: Icon, value, label }) {
  return (
    <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
      <Icon size={12} className="text-gray-400 dark:text-gray-500" aria-hidden="true" />
      <span className="font-medium text-gray-800 dark:text-gray-100">{value}</span>
      <span className="text-gray-400 dark:text-gray-500">{label}</span>
    </div>
  );
}

/**
 * Scheda di una VM: intestazione con stato e identità, prodotti, descrizione,
 * micro-metriche, azioni disponibili e dettaglio completo espandibile
 * (lazy-load del sistema operativo/datastore dall'endpoint di dettaglio).
 */
export default function VmCard({
  vm,
  catalogWarnings,
  op,
  busy,
  onRequestAction,
  onDismissOp,
}) {
  const [expanded, setExpanded] = useState(false);
  const { detail, loading: detailLoading } = useVmDetail(vm.catalogId, {
    enabled: expanded,
  });
  const merged = detail ? { ...vm, ...detail } : vm;

  const meta = powerStateMeta(vm.powerState);
  const res = vm.resources || {};
  const guest = vm.guest || {};

  return (
    <Card className="flex flex-col p-4">
      {/* Intestazione */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span
            className={`mt-1 h-3 w-3 shrink-0 rounded-full ${meta.dot}`}
            aria-hidden="true"
          />
          <div className="min-w-0">
            <h3 className="truncate text-base font-semibold text-gray-900 dark:text-white">
              {vm.name}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {vm.environment || 'Senza ambiente'}
              {vm.role ? ` · ${vm.role}` : ''}
            </p>
          </div>
        </div>
        <PowerStateBadge state={vm.powerState} />
      </div>

      {/* Prodotti + identità */}
      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        <ProductBadges vm={vm} />
        <IdentityBadge vm={vm} catalogWarnings={catalogWarnings} />
      </div>

      {/* Descrizione */}
      {vm.description && (
        <p className="mt-2 line-clamp-2 text-xs text-gray-600 dark:text-gray-300">
          {vm.description}
        </p>
      )}

      {/* Micro-metriche */}
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 border-t border-gray-100 pt-3 dark:border-gray-700">
        <div className="text-xs">
          <VmIpAddress vm={vm} showIcon />
        </div>
        <MiniStat icon={FaMicrochip} value={res.vcpus ?? '—'} label="vCPU" />
        <MiniStat icon={FaMemory} value={formatMemoryMb(res.memoryMb)} label="RAM" />
        {res.cpuUsageMhz != null && (
          <MiniStat icon={FaMicrochip} value={formatMhz(res.cpuUsageMhz)} label="uso" />
        )}
        {vm.powerState === 'ON' && res.uptimeSeconds != null && (
          <MiniStat icon={FaClock} value={formatUptime(res.uptimeSeconds)} label="" />
        )}
        {guest.heartbeatStatus && (
          <HeartbeatIndicator status={guest.heartbeatStatus} />
        )}
      </div>

      {/* Dettaglio espandibile */}
      <div className="mt-3">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
        >
          <FaChevronDown
            size={11}
            aria-hidden="true"
            className={`transition-transform motion-reduce:transition-none ${expanded ? 'rotate-180' : ''}`}
          />
          {expanded ? 'Nascondi dettagli' : 'Dettagli completi'}
        </button>
        {expanded && (
          <div className="mt-3 rounded-xl bg-gray-50 p-3 dark:bg-gray-900/40">
            {detailLoading && (
              <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                <Spinner className="h-3.5 w-3.5" /> Caricamento dettagli…
              </div>
            )}
            <VmDetailFields vm={merged} />
          </div>
        )}
      </div>

      {/* Azioni */}
      <div className="mt-4 border-t border-gray-100 pt-3 dark:border-gray-700">
        <VmActions
          vm={vm}
          catalogWarnings={catalogWarnings}
          op={op}
          busy={busy}
          onRequestAction={onRequestAction}
          onDismissOp={onDismissOp}
        />
      </div>
    </Card>
  );
}
